import React, { useState, useEffect, useCallback } from "react"
import { DataGrid } from "@mui/x-data-grid"
import { EditOutlined, DeleteOutlined, Add } from "@mui/icons-material"
import { Button, IconButton, Typography, Box, CircularProgress } from "@mui/material"
import { useNavigate } from "react-router-dom"
import api from "../../services/api"
import { API_ENDPOINTS } from "../../config/api"
import { hasPermission, PERMISSIONS } from "../../utils/permissions"
import { useAuth } from "../../context/AuthContext"

const Roles = () => {
  const navigate = useNavigate()
  const { user } = useAuth();  // Get the whole user object
  const userPermissions = user?.permissions;
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [totalItems, setTotalItems] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(5)

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true)
      const response = await api.get(API_ENDPOINTS.ROLE.GET_ALL, {
        params: { page: page + 1, limit: pageSize },
      })
      setRoles(response.data.roles)
      setTotalItems(response.data.totalItems)
      setLoading(false)
    } catch (err) {
      console.error("Error fetching roles:", err)
      setError("Failed to fetch roles")
      setLoading(false)
    }
  }, [page, pageSize])

  useEffect(() => {
    fetchRoles()
  }, [fetchRoles])

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this role?")) {
      try {
        await api.delete(API_ENDPOINTS.ROLE.DELETE(id))
        fetchRoles()
      } catch (err) {
        console.error("Error deleting role:", err)
        setError("Failed to delete role")
      }
    }
  }

  const columns = [
    { field: "name", headerName: "Name", flex: 0.3, minWidth: 150 },
    { field: "description", headerName: "Description", flex: 0.4, minWidth: 200 },
    {
      field: "created_at",
      headerName: "Created at",
      flex: 0.2,
      minWidth: 180,
      valueGetter: (params) => {
        if (!params) return "-"
        const date = new Date(params)
        return date instanceof Date && !isNaN(date.getTime())
          ? date.toLocaleString("id-ID", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
          : "-"
      },
    },
    ...(hasPermission(userPermissions, 'role', PERMISSIONS.UPDATE) || hasPermission(userPermissions, 'role', PERMISSIONS.DELETE) ? [{
      field: "actions",
      headerName: "Actions",
      flex: 0.1,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          {hasPermission(userPermissions, 'role', PERMISSIONS.UPDATE) && (
            <IconButton size="small" sx={{ color: "#6C757D" }} onClick={() => navigate(`/roles/edit/${params.row.id}`)}>
              <EditOutlined fontSize="inherit" />
            </IconButton>
          )}
          {hasPermission(userPermissions, 'role', PERMISSIONS.DELETE) && (
            <IconButton size="small" sx={{ color: "#E31B54" }} onClick={() => handleDelete(params.row.id)}>
              <DeleteOutlined fontSize="inherit" />
            </IconButton>
          )}
        </Box>
      ),
    }] : [])
  ]

  if (loading) return <CircularProgress />
  if (error) return <Typography color="error">{error}</Typography>

  return (
    <Box sx={{ width: "100%", pr: 2, pt: 0, backgroundColor: "background.paper", borderRadius: 1, mb: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
          Roles
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          {hasPermission(userPermissions, 'role', PERMISSIONS.CREATE) && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => navigate("/roles/create")}
              sx={{
                textTransform: "none",
                backgroundColor: "#E31B54",
                color: "#FFFFFF",
                minWidth: "120px",
                px: 2,
                height: "32px",
                borderRadius: "16px",
                "&:hover": {
                  backgroundColor: "#C41747",
                },
              }}
            >
              New role
            </Button>
          )}
        </Box>
      </Box>
      <DataGrid
        rows={roles}
        columns={columns}
        pagination
        paginationMode="server"
        rowCount={totalItems}
        pageSizeOptions={[5, 10, 25]}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(newModel) => {
          setPage(newModel.page)
          setPageSize(newModel.pageSize)
        }}
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
        disableColumnMenu
        sx={{
          borderRadius: 6,
          borderColor: "background.primary",
          boxShadow: 1,
          color: "text.secondary",
          bgcolor: "background.paper",
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-columnHeader": {
            "&:focus": {
              outline: "none",
            },
            "&:focus-within": {
              outline: "none",
            },
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid",
            borderColor: "background.default",
          },
        }}
      />
    </Box>
  )
}

export default Roles
